import type { SortingState } from '@tanstack/react-table';
import { Table, type TableRowProps } from 'common/Table';
import { useCaseStudyParamsSuffix } from 'hooks/studyParams';
import { useFocusMode } from 'hooks/useFocusMode';
import { useReportTemplateValue } from 'hooks/useReportTemplate';
import { telemetryContextFor } from 'modules/Apollo/types';
import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Colors } from 'styles';
import { rootTheme } from 'theme/rootTheme';
import { useHasClinicLevelTemplatePermission } from '../hooks/useHasClinicLevelTemplatePermission';
import { useRightDrawerTableColumns } from './useRightDrawerTableColumns';
import { RightDrawerAction } from './types';
import type { RightDrawerTableItemType, RightDrawerTabType } from './types';
import { orderStringToSortingState, sortingStateToOrderString } from './sorting';
import { RightDrawerTableRow } from './RightDrawerTableRow';

type RightDrawerTableProps = {
  items: Array<RightDrawerTableItemType>;
  activeTab: RightDrawerTabType;
  basePath: string;
  starredId: Nullable<string>;
  isLoading: boolean;
  hasMoreData: boolean;
  onLoadMore: () => void;
  onRowClick: (arg1: RightDrawerTableItemType) => Promise<void>;
  onRowDoubleClick: (arg1: RightDrawerTableItemType) => Promise<void>;
  sorting?: string;
  onSortingChange?: (arg1: string) => void;
  isInsertColumnEnabled?: boolean;
};

export const RightDrawerTable = memo(
  ({
    items,
    activeTab,
    basePath,
    starredId,
    onRowClick,
    isLoading,
    onLoadMore,
    hasMoreData,
    onRowDoubleClick,
    sorting,
    onSortingChange,
    isInsertColumnEnabled = false,
  }: RightDrawerTableProps): React.ReactElement | null => {
    const priorsSuffix = useCaseStudyParamsSuffix();
    const navigate = useNavigate();
    const { isFocusModeOn } = useFocusMode({ context: APOLLO_QUERY_CONTEXT });
    const hasClinicLevelTemplatePermission = useHasClinicLevelTemplatePermission({
      context: APOLLO_QUERY_CONTEXT,
    });
    const template = useReportTemplateValue();

    const sortingState = useMemo(() => orderStringToSortingState(sorting), [sorting]);

    const handleSortingChange = useCallback(
      (nextSortingState: SortingState) => {
        const sortingString = sortingStateToOrderString(nextSortingState);
        if (sortingString && onSortingChange) onSortingChange(sortingString);
      },
      [onSortingChange]
    );

    const getRowAction = useCallback((item: RightDrawerTableItemType) => {
      return {
        id: item.id,
        name: item.name,
        action:
          item.owner != null && item.id != null ? RightDrawerAction.Edit : RightDrawerAction.Clone,
      } as const;
    }, []);

    const columns = useRightDrawerTableColumns({
      activeTab,
      basePath,
      priorsSuffix,
      isFocusModeOn,
      hasClinicLevelTemplatePermission,
      navigate,
      starredId,
      onSetSorting: handleSortingChange,
      isInsertColumnEnabled,
      onRowDoubleClick,
      getRowAction,
    });

    const TableRow = useMemo(
      () =>
        function MemoizedDefaultRow(props: TableRowProps<RightDrawerTableItemType>) {
          return (
            <RightDrawerTableRow
              {...props}
              activeTab={activeTab}
              getRowAction={getRowAction}
              onRowClick={onRowClick}
              onRowDoubleClick={onRowDoubleClick}
              selectedTemplateId={template?.id}
            />
          );
        },
      [activeTab, getRowAction, onRowClick, onRowDoubleClick, template?.id]
    );

    return (
      <Table
        data-testid={`${activeTab}-table`}
        css={`
          background-color: ${Colors.gray1};
          border-radius: ${rootTheme.radius.sm}px;

          .table-header {
            background-color: ${Colors.gray2};
            padding: 0px 16px 0px 0px;
            border-radius: ${rootTheme.radius.sm}px ${rootTheme.radius.sm}px 0px 0px;
          }
        `}
        columns={columns}
        data={items}
        Row={TableRow}
        hasNextPage={hasMoreData}
        loading={isLoading}
        state={{ sorting: sortingState }}
        manualSorting
        onLoadMore={onLoadMore}
      />
    );
  }
);

RightDrawerTable.displayName = 'RightDrawerTable';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(RightDrawerTable);
